import {
  InventoryOverride,
  InventoryResponse,
} from "../../../../reducers/componentSearch";
import { FieldArray, Formik } from "formik";
import {
  FakeLink,
  FormContainer,
  PriceBreaksFormContainer,
} from "../../../../constants/Styles";
import FormikFormLine from "../../../formik-form-components/FormikFormLine";
import FormikNumberInput from "../../../formik-form-components/FormikNumberInput";
import { FieldTypes } from "../../../formik-form-components/FormikFormLine/constants";
import ContainerFormButtons from "./PhysicalInventory/ContainerFormButtons";
import React, { useEffect, useState } from "react";
import settings from "../../../../constants/settings";
import * as Yup from "yup";
import {
  todayInPhillyToDateInputString,
  twentyFourHourToTwelveHour,
} from "../../../../utils/valueFormatting";
import useFetchAndTrace from "../../../../utils/hooks/fetchWithTrace";
import Button from "../../../common/Button";
import { useFlashPCBSettings } from "../../../../utils/hooks/commonly-stored/flashPCBSettings";
import LoadingIcon from "../../../common/LoadingIcon";
import { PriceInfo } from "../../../../reducers/models/parts";
import { useCustomAlert } from "../../../../utils/hooks/alerts";

const OVERRIDE_SCHEMA = Yup.object().shape({
  inventory_level: Yup.number().min(0),
  minimum_order_quantity: Yup.number().min(1),
  expiration: Yup.date().min(new Date(), "Please choose future date"),
  price_breaks: Yup.array()
    .of(
      Yup.object().shape({
        unit_price: Yup.number().min(0, "Field cannot be negative").required(),
        quantity: Yup.number()
          .min(1, "Field must be positive")
          .integer()
          .required(),
        currency: Yup.string().oneOf(["USD"]),
      }),
    )
    .required()
    .test("Price breaks", "Price breaks are invalid", (priceBreaks) => {
      if (priceBreaks.length > 1) {
        for (let i = 1; i < priceBreaks.length; i++) {
          const prevBreak = priceBreaks[i - 1] as PriceInfo;
          const priceBreak = priceBreaks[i] as PriceInfo;
          if (prevBreak.unit_price <= priceBreak.unit_price) {
            return false;
          }
          if (prevBreak.quantity >= priceBreak.quantity) {
            return false;
          }
        }
      }
      return true;
    }),
});

export function useUpdateInventoryOverride(
  onFinishedEditing: () => void,
  partId: string,
) {
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const errorHandler = () => {
    setErrorMessage("Failed to adjust inventory");
  };

  const overrideHandler = (response: Response) => {
    setLoading(false);
    if (response.ok) {
      onFinishedEditing();
    } else {
      throw new Error("Unable to override virtual inventory");
    }
  };

  const updateInventoryOverride = (values: InventoryOverride | null) => {
    const url = `${settings.api_url}/parts/${partId}/inventory/virtual`;
    setErrorMessage("");
    setLoading(true);
    void fetchAndTrace(url, overrideHandler, errorHandler, {
      body: JSON.stringify({ override: values }),
      method: "PUT",
    });
  };

  return {
    loading,
    errorMessage,
    updateInventoryOverride,
  };
}

const DEFAULT_PRICE_BREAK: PriceInfo = {
  unit_price: 0,
  quantity: 1,
  currency: "USD",
};
export default function OverrideVirtualInventory({
  inventoryResponse,
  partId,
  onCancel,
  onFinishedEditing,
}: {
  inventoryResponse: InventoryResponse;
  partId: string;
  onCancel: () => void;
  onFinishedEditing: () => void;
}) {
  const flashPCBSettings = useFlashPCBSettings();
  const { loading, errorMessage, updateInventoryOverride } =
    useUpdateInventoryOverride(onFinishedEditing, partId);
  const customAlert = useCustomAlert();

  useEffect(() => {
    if (errorMessage) {
      customAlert(errorMessage);
    }
  }, [customAlert, errorMessage]);

  if (flashPCBSettings === null) {
    return <LoadingIcon />;
  }

  const initialValues = () => {
    const defaultExpiration = todayInPhillyToDateInputString(7);
    const defaultValues: InventoryOverride = {
      inventory_level: 0,
      minimum_order_quantity: 1,
      expiration: defaultExpiration,
      price_breaks: [DEFAULT_PRICE_BREAK],
      notes: "",
    };
    if (inventoryResponse.virtual_inventory.override_history) {
      const mostRecentOverride =
        inventoryResponse.virtual_inventory.override_history.at(-1);
      if (mostRecentOverride && mostRecentOverride.override) {
        return {
          ...mostRecentOverride.override,
          expiration: defaultExpiration,
        };
      } else {
        return defaultValues;
      }
    } else {
      return defaultValues;
    }
  };
  return (
    <Formik
      initialValues={initialValues()}
      onSubmit={updateInventoryOverride}
      validationSchema={OVERRIDE_SCHEMA}
    >
      {({ handleSubmit, isValid, values }) => {
        const numPriceBreaks = values.price_breaks.length;
        const priceBreak = values.price_breaks[numPriceBreaks - 1] as PriceInfo;
        return (
          <FormContainer>
            <FormikNumberInput
              name="inventory_level"
              label="Inventory override level"
            />
            <FormikNumberInput
              name="minimum_order_quantity"
              label="Minimum order quantity"
            />
            <FormikFormLine
              name={"expiration"}
              label={`This override expires at ${twentyFourHourToTwelveHour(flashPCBSettings.local_lead_time_eod)} on`}
              type={FieldTypes.date}
            />
            <FormikFormLine
              name={"notes"}
              label={"Internal only notes (put sourcing info here)"}
              isTextArea
            />
            <FieldArray
              name="price_breaks"
              render={(arrayHelpers) => (
                <PriceBreaksFormContainer>
                  Price breaks:
                  {values.price_breaks.map((priceInfo, priceIndex) => {
                    const firstPriceBreak = priceIndex == 0;
                    return (
                      <div key={`${priceIndex}`}>
                        <FormikNumberInput
                          name={`price_breaks.${priceIndex}.unit_price`}
                          label={`Price in ${priceInfo.currency}`}
                        />
                        <FormikNumberInput
                          name={`price_breaks.${priceIndex}.quantity`}
                          label={
                            firstPriceBreak
                              ? "At quantity (first break must have quantity 1)"
                              : "At quantity"
                          }
                          disabled={firstPriceBreak}
                        />
                        {!firstPriceBreak && (
                          <FakeLink
                            onClick={() => arrayHelpers.remove(priceIndex)}
                          >
                            Remove
                          </FakeLink>
                        )}
                      </div>
                    );
                  })}
                  <div>
                    <Button
                      isSmallSize
                      label={"Add price break"}
                      onClick={() =>
                        arrayHelpers.push(
                          numPriceBreaks > 0
                            ? {
                                unit_price: priceBreak.unit_price - 0.01,
                                quantity: priceBreak.quantity + 1,
                                currency: priceBreak.currency,
                              }
                            : DEFAULT_PRICE_BREAK,
                        )
                      }
                    />
                  </div>
                </PriceBreaksFormContainer>
              )}
            />
            <ContainerFormButtons
              onCancel={onCancel}
              loading={loading}
              isDisabled={!isValid}
              onSubmit={handleSubmit}
            />
          </FormContainer>
        );
      }}
    </Formik>
  );
}
